import { MatchAngaben, SifaMitarbeiter } from '../../services/sifa/types';

/**
 * CSV-Import des Mitarbeiterstamms — rein, ohne DOM, ohne Supabase.
 *
 * Erwartete Spalten: Vorname;Nachname;Geburtsdatum;E-Mail;Abteilung.
 * Trennzeichen Semikolon, Komma oder Tabulator (das Excel des Kunden
 * entscheidet, nicht wir); eine Kopfzeile wird erkannt und uebersprungen,
 * Datumsformate dd.mm.yyyy und yyyy-mm-dd. Was nicht lesbar ist, wird als
 * Fehler mit Zeilennummer gemeldet und NICHT stillschweigend angelegt.
 */

export interface MitarbeiterImportZeile {
  /** 1-basiert, wie im Editor des Anwenders. */
  zeile: number;
  vorname: string;
  nachname: string;
  /** ISO YYYY-MM-DD oder null. */
  geburtsdatum: string | null;
  email: string | null;
  abteilung: string | null;
}

export interface MitarbeiterImportErgebnis {
  zeilen: MitarbeiterImportZeile[];
  fehler: string[];
}

const KOPF_WOERTER = ['vorname', 'nachname', 'name', 'geburtsdatum', 'geburtstag', 'e-mail', 'email', 'mail', 'abteilung'];

/** dd.mm.yyyy oder yyyy-mm-dd → ISO; alles andere null. Auch d.m.yyyy und yyyy-m-d. */
export const normalisiereDatum = (roh: string | null | undefined): string | null => {
  const s = (roh || '').trim();
  if (!s) return null;
  let j: number; let m: number; let t: number;
  let tref = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (tref) { t = +tref[1]; m = +tref[2]; j = +tref[3]; }
  else {
    tref = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:T.*)?$/.exec(s);
    if (!tref) return null;
    j = +tref[1]; m = +tref[2]; t = +tref[3];
  }
  if (m < 1 || m > 12 || t < 1 || t > 31 || j < 1900 || j > 2100) return null;
  // Tag-im-Monat pruefen ueber UTC, damit kein Zeitzonenversatz kippt.
  const d = new Date(Date.UTC(j, m - 1, t));
  if (d.getUTCMonth() !== m - 1 || d.getUTCDate() !== t) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${j}-${p(m)}-${p(t)}`;
};

const normalisiereEmail = (roh: string | null | undefined): string | null => {
  const s = (roh || '').trim().toLowerCase();
  return s || null;
};

/** Trennzeichen aus der ersten nicht-leeren Zeile: das haeufigste von ; , Tab. */
export const erkenneTrenner = (text: string): ';' | ',' | '\t' => {
  const erste = (text || '').split(/\r?\n/).find((z) => z.trim().length > 0) || '';
  const kandidaten: Array<';' | ',' | '\t'> = [';', '\t', ','];
  let best: ';' | ',' | '\t' = ';';
  let bestN = -1;
  for (const k of kandidaten) {
    const n = erste.split(k).length - 1;
    if (n > bestN) { bestN = n; best = k; }
  }
  return best;
};

/** Eine CSV-Zeile in Felder — mit Anfuehrungszeichen („a;b" in "…" bleibt ein Feld). */
export const teileZeile = (zeile: string, trenner: string): string[] => {
  const felder: string[] = [];
  let aktuell = '';
  let inAnf = false;
  for (let i = 0; i < zeile.length; i += 1) {
    const c = zeile[i];
    if (c === '"') {
      if (inAnf && zeile[i + 1] === '"') { aktuell += '"'; i += 1; }
      else inAnf = !inAnf;
    } else if (c === trenner && !inAnf) {
      felder.push(aktuell);
      aktuell = '';
    } else {
      aktuell += c;
    }
  }
  felder.push(aktuell);
  return felder.map((f) => f.trim());
};

const istKopfzeile = (felder: string[]): boolean => {
  const kleine = felder.map((f) => f.toLowerCase());
  return kleine.filter((f) => KOPF_WOERTER.some((w) => f.includes(w))).length >= 2;
};

export const parseMitarbeiterCsv = (text: string): MitarbeiterImportErgebnis => {
  const zeilen: MitarbeiterImportZeile[] = [];
  const fehler: string[] = [];
  const roh = (text || '').replace(/^﻿/, '');
  if (!roh.trim()) return { zeilen, fehler: ['Die Eingabe ist leer.'] };
  const trenner = erkenneTrenner(roh);
  const rohZeilen = roh.split(/\r?\n/);

  let kopfGeprueft = false;
  rohZeilen.forEach((rz, i) => {
    if (!rz.trim()) return;
    const felder = teileZeile(rz, trenner);
    if (!kopfGeprueft) {
      kopfGeprueft = true;
      if (istKopfzeile(felder)) return;
    }
    const [vorname = '', nachname = '', geburt = '', email = '', abteilung = ''] = felder;
    const nr = i + 1;
    if (!vorname.trim() || !nachname.trim()) {
      fehler.push(`Zeile ${nr}: Vorname und Nachname sind Pflicht.`);
      return;
    }
    const geburtsdatum = normalisiereDatum(geburt);
    if (geburt.trim() && !geburtsdatum) {
      fehler.push(`Zeile ${nr}: Geburtsdatum „${geburt.trim()}" nicht lesbar (dd.mm.yyyy oder yyyy-mm-dd).`);
      return;
    }
    const mail = normalisiereEmail(email);
    if (mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      fehler.push(`Zeile ${nr}: E-Mail „${email.trim()}" sieht nicht nach einer Adresse aus.`);
      return;
    }
    zeilen.push({
      zeile: nr,
      vorname: vorname.trim(),
      nachname: nachname.trim(),
      geburtsdatum,
      email: mail,
      abteilung: abteilung.trim() || null,
    });
  });

  return { zeilen, fehler };
};

/** Ergebnis der Dublettenpruefung je Zeile — Vorschau, bevor angelegt wird. */
export interface MitarbeiterImportGeprueft extends MitarbeiterImportZeile {
  /** Bestehende Person, mit der die Zeile zusammenfaellt (Regel „3 von 4"). */
  dublette: SifaMitarbeiter | null;
  /** Zeile derselben Datei, die dieselbe Person schon traegt. */
  doppeltInDatei: number | null;
}

/** Was findeMitarbeiter (services/sifa/mitarbeiterMatch) zurueckgibt — nur der Teil, der hier gebraucht wird. */
export type FindeMitarbeiter = (angaben: MatchAngaben, bestand: SifaMitarbeiter[]) => { treffer: SifaMitarbeiter | null };

/**
 * Markiert Dubletten gegen den Bestand UND innerhalb der Datei. `finde` wird
 * injiziert, damit die Funktion ohne die Match-Regel pruefbar bleibt; im
 * Modul ist das findeMitarbeiter aus services/sifa.
 *
 * Innerhalb der Datei zaehlt eine Zeile als doppelt, wenn sie gegen die
 * bereits geprueften (nicht-dubletten) Zeilen als Treffer gilt — dafuer wird
 * aus jeder angenommenen Zeile ein Schein-Mitarbeiter gebildet.
 */
export const markiereDubletten = (
  zeilen: MitarbeiterImportZeile[],
  bestand: SifaMitarbeiter[],
  finde: FindeMitarbeiter,
): MitarbeiterImportGeprueft[] => {
  const angenommen: Array<SifaMitarbeiter & { zeile: number }> = [];
  return zeilen.map((z) => {
    const angaben: MatchAngaben = { vorname: z.vorname, nachname: z.nachname, geburtsdatum: z.geburtsdatum, email: z.email };
    const dublette = finde(angaben, bestand).treffer;
    let doppeltInDatei: number | null = null;
    if (!dublette) {
      const inDatei = finde(angaben, angenommen).treffer as (SifaMitarbeiter & { zeile: number }) | null;
      if (inDatei) doppeltInDatei = inDatei.zeile;
      else {
        angenommen.push({
          id: `import-${z.zeile}`,
          projectId: '',
          vorname: z.vorname,
          nachname: z.nachname,
          geburtsdatum: z.geburtsdatum,
          email: z.email,
          abteilung: z.abteilung,
          createdAt: '',
          updatedAt: '',
          zeile: z.zeile,
        });
      }
    }
    return { ...z, dublette, doppeltInDatei };
  });
};

/** Nur die Zeilen, die wirklich angelegt werden. */
export const nurNeue = (geprueft: MitarbeiterImportGeprueft[]): MitarbeiterImportGeprueft[] =>
  geprueft.filter((z) => !z.dublette && z.doppeltInDatei === null);

export const BEISPIEL_CSV = 'Vorname;Nachname;Geburtsdatum;E-Mail;Abteilung\nMax;Mustermann;12.03.1985;max.mustermann@firma.de;Lager\nErika;Musterfrau;1990-07-01;erika@firma.de;Verwaltung';
